import { useCallback, useEffect, useRef, useState } from 'react';
import {
  filterOptionsWithWindow,
  getSelectNoOptionsMessage,
  isCompactOptionsList,
  LIST_WINDOW_SIZE,
} from './selectSearchFilter';

const SCROLL_LOAD_THRESHOLD = 48;

export function useAutocompleteListSearch({
  optionsCount,
  selectedLabel = '',
  multiSelect = false,
  matchFn,
  sortFn,
  getSortLabel,
  listMaxHeight = 320,
}) {
  const [listWindow, setListWindow] = useState(LIST_WINDOW_SIZE);
  const [searchInput, setSearchInput] = useState('');
  const inputValueRef = useRef('');

  useEffect(() => {
    if (multiSelect) {
      return;
    }

    const label = selectedLabel ?? '';
    setSearchInput(label);
    inputValueRef.current = label;
  }, [selectedLabel, multiSelect]);

  const onInputChange = useCallback((_event, newInputValue) => {
    inputValueRef.current = newInputValue;
    setSearchInput(newInputValue);
    if (!newInputValue.trim()) {
      setListWindow(LIST_WINDOW_SIZE);
    }
  }, []);

  const onClose = useCallback(() => {
    setListWindow(LIST_WINDOW_SIZE);
  }, []);

  const compactList = isCompactOptionsList(optionsCount);

  const handleListboxScroll = useCallback(
    (event) => {
      if (compactList || inputValueRef.current.trim()) {
        return;
      }

      const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
      if (scrollTop + clientHeight >= scrollHeight - SCROLL_LOAD_THRESHOLD) {
        setListWindow((prev) => {
          if (prev >= optionsCount) {
            return prev;
          }
          return Math.min(prev + LIST_WINDOW_SIZE, optionsCount);
        });
      }
    },
    [compactList, optionsCount]
  );

  const filterOptions = useCallback(
    (options) =>
      filterOptionsWithWindow(options, searchInput, {
        loadedCount: listWindow,
        matchFn,
        sortFn,
        getSortLabel,
      }),
    [listWindow, searchInput, matchFn, sortFn, getSortLabel]
  );

  const noOptionsText = getSelectNoOptionsMessage(searchInput, optionsCount);

  const listboxSlotProps = {
    onScroll: handleListboxScroll,
    sx: { maxHeight: listMaxHeight, overflow: 'auto' },
  };

  return {
    searchInput,
    inputValue: multiSelect ? undefined : searchInput,
    onInputChange,
    onClose,
    filterOptions,
    listboxSlotProps,
    noOptionsText,
  };
}
